import messageNotificationAsset from './assets/message-notification.mp3';
import { readMessageSoundsEnabled } from './messageSoundPreferences';

/** @typedef {'activeChat' | 'backgroundMessage' | 'newConversation'} MessageSoundType */

const SOUND_PROFILES = {
  activeChat: { volume: 0.45 },
  backgroundMessage: { volume: 0.65 },
  newConversation: { volume: 0.85 },
};

let audioInstance = null;
let unlocked = false;
let enabled = readMessageSoundsEnabled();
let isPlaying = false;
let queuedSounds = [];

function getAudioInstance() {
  if (typeof window === 'undefined') return null;

  if (!audioInstance) {
    audioInstance = new Audio(messageNotificationAsset);
    audioInstance.preload = 'auto';
  }

  return audioInstance;
}

function playNextQueuedSound() {
  const audio = getAudioInstance();
  if (!audio || !enabled || !unlocked) {
    queuedSounds = [];
    isPlaying = false;
    return;
  }

  const type = queuedSounds.shift();
  if (!type) {
    isPlaying = false;
    return;
  }

  const profile = SOUND_PROFILES[type] || SOUND_PROFILES.backgroundMessage;
  isPlaying = true;
  audio.volume = profile.volume;
  audio.currentTime = 0;
  audio.onended = () => {
    isPlaying = false;
    playNextQueuedSound();
  };

  const attempt = audio.play();
  if (attempt && typeof attempt.catch === 'function') {
    attempt.catch(() => {
      isPlaying = false;
      playNextQueuedSound();
    });
  }
}

/**
 * Unlocks browser audio after the first user gesture (required by autoplay policies).
 */
export function unlockMessageSound() {
  if (unlocked || typeof window === 'undefined') return;

  const audio = getAudioInstance();
  if (!audio) return;

  audio.volume = 0;
  const attempt = audio.play();
  unlocked = true;

  if (attempt && typeof attempt.then === 'function') {
    attempt
      .then(() => {
        audio.pause();
        audio.currentTime = 0;
        audio.volume = SOUND_PROFILES.activeChat.volume;
      })
      .catch(() => {
        unlocked = false;
      });
    return;
  }

  audio.pause();
  audio.currentTime = 0;
  audio.volume = SOUND_PROFILES.activeChat.volume;
}

export function setMessageSoundsEnabled(nextEnabled) {
  enabled = Boolean(nextEnabled);
}

export function isMessageSoundEnabled() {
  return enabled;
}

export function refreshMessageSoundPreference() {
  enabled = readMessageSoundsEnabled();
}

/**
 * Plays a tiered notification sound for inbound chat activity.
 */
export function playMessageSound(type = 'backgroundMessage') {
  if (!enabled || !unlocked || !getAudioInstance()) return false;

  queuedSounds.push(type);
  if (!isPlaying) playNextQueuedSound();
  return true;
}

export function resetMessageSoundForTests() {
  audioInstance = null;
  unlocked = false;
  enabled = true;
  isPlaying = false;
  queuedSounds = [];
}
